import { LayoutCanvas } from '../../types/types';
import { LAYOUT_CANVAS_DEFAULTS } from '../../constants';

// Replaces cleared values (undefined/null/''/NaN) in a canvas patch so the canvas
// always keeps every key filled: known optional keys fall back to their default,
// keys without a default (width/height/backgroundColor/custom) keep the current value.
// Numeric keys sent as strings (the API returns e.g. scaleX: "1,2") are converted
// to numbers, with the decimal comma translated.
export const sanitizeCanvasPatch = (
  patch: Partial<LayoutCanvas>,
  current: LayoutCanvas
): Partial<LayoutCanvas> => {
  const sanitized: Partial<LayoutCanvas> = {};

  Object.entries(patch).forEach(([key, value]) => {
    // backgroundImage: null is a meaningful value — the server keeps the stored
    // bitmap when the key is omitted, deleting requires the explicit null.
    if (key === 'backgroundImage') {
      sanitized.backgroundImage =
        value === undefined || value === '' ? null : (value as string | null);
      return;
    }

    const defaultValue =
      key in LAYOUT_CANVAS_DEFAULTS
        ? LAYOUT_CANVAS_DEFAULTS[key as keyof typeof LAYOUT_CANVAS_DEFAULTS]
        : undefined;

    const normalized =
      typeof defaultValue === 'number' &&
      typeof value === 'string' &&
      value !== ''
        ? Number(value.replace(',', '.'))
        : value;

    const isEmpty =
      normalized === undefined ||
      normalized === null ||
      normalized === '' ||
      (typeof normalized === 'number' && Number.isNaN(normalized));

    if (!isEmpty) {
      sanitized[key] = normalized;
      return;
    }

    sanitized[key] = defaultValue !== undefined ? defaultValue : current[key];
  });

  return sanitized;
};
